<template>
  <div class="action-logs-list-page-component">
    <VdPage title="Action logs" :loading="loading">
      <vd-empty v-if="noLogs" title="No logs">
        <div>
          <p>Sorry, there is no data.</p>
          <p>Please come back later, after having performed some actions (class creation, config update...)</p>
        </div>
      </vd-empty>
      <div v-else>
        <div v-for="{ versionId: id, version, actionLogs } in actionLogsWithVersions" :key="version ? version.id : id">
          <ActionLogSection
            v-if="version"
            class="action-log-section"
            :game-id="gameId"
            :version="version"
            :logs="actionLogs"
          />
          <div v-else><em>version not found</em></div>
        </div>
      </div>

      <div class="pagination">
        <vd-button v-if="hasNextPage" :loading="loadMoreLoading" type="secondary" @click="loadMore">
          Show more
        </vd-button>
      </div>
    </VdPage>
  </div>
</template>

<script lang="ts">
import { VdPage } from "@voodoo.io/voodoo-ui";
import { defineComponent, computed } from "vue";

import ActionLogSection from "@/components/ActionLogSection/ActionLogSection.vue";
import { ActionLogsListPageActionLogFragment, useActionLogsListPageQuery, useRelayConnection } from "@/graphql";

export default defineComponent({
  name: "ActionLogPage",

  components: {
    ActionLogSection,
    VdPage,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },

    versionId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    /**
     * Load all the logs
     */
    const { result, loading, fetchMore } = useActionLogsListPageQuery(
      { gameId: props.gameId, first: 100 },
      { fetchPolicy: "network-only" }
    );

    const {
      list: actionLogs,
      loading: loadMoreLoading,
      loadMore,
      hasNextPage,
    } = useRelayConnection(fetchMore, result, (res) => res?.game?.actionLogs);

    /**
     * All version of the game
     */
    const versions = computed(() => result.value?.game?.versions || []);

    const actionLogsWithVersions = computed(() => {
      const logsByVersionId = actionLogs.value.reduce(
        (acc: Record<string, ActionLogsListPageActionLogFragment[]>, actionLog) => {
          if (!acc[actionLog.versionId]) {
            return { ...acc, [actionLog.versionId]: [actionLog] };
          }
          return { ...acc, [actionLog.versionId]: [...acc[actionLog.versionId], actionLog] };
        },
        {}
      );

      return Object.entries(logsByVersionId).map(([versionId, logs]) => ({
        versionId,
        version: versions.value.find((o) => o.id === versionId),
        actionLogs: logs,
      }));
    });

    const noLogs = computed(() => actionLogs.value.length === 0);

    return {
      actionLogsWithVersions,
      loading,
      noLogs,
      loadMoreLoading,
      loadMore,
      hasNextPage,
    };
  },
});
</script>

<style lang="scss" scoped>
.action-logs-list-page-component {
  .action-log-section {
    margin-top: size("XXL");

    &:first-of-type {
      margin-top: 0;
    }
  }

  .pagination {
    margin-top: size("L");
    margin-bottom: size("XXL");
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
